import React, { useState, useEffect } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { API_URL } from '../../config';

interface ReportStatus {
    date: string;
    label: string;
    status: 'AVAILABLE' | 'MISSING';
    id?: number;
    url?: string;
}

interface AuditResult {
    daily: ReportStatus[];
    weekly: ReportStatus[];
    monthly: ReportStatus[];
    quarterly: ReportStatus[];
}

const AdminReportsPage: React.FC = () => {
    const [auditData, setAuditData] = useState<AuditResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState<string | null>(null); // Date currently being regenerated
    const [selectedReportUrl, setSelectedReportUrl] = useState<string | null>(null);

    const fetchAudit = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${API_URL}/api/reports/audit?months=3`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setAuditData(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAudit();
    }, []);

    const regenerateReport = async (type: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY', dateStr: string) => {
        setProcessing(dateStr);
        try {
            const token = localStorage.getItem('access_token');
            let endpoint = '';

            if (type === 'DAILY') {
                endpoint = `${API_URL}/api/reports/briefings/generate?date_str=${dateStr.split('T')[0]}`;
            } else if (type === 'WEEKLY') {
                endpoint = `${API_URL}/api/reports/periodic/generate-weekly?date_str=${dateStr.split('T')[0]}`;
            } else if (type === 'MONTHLY') {
                endpoint = `${API_URL}/api/reports/periodic/generate-monthly?date_str=${dateStr.split('T')[0]}`;
            } else if (type === 'QUARTERLY') {
                const dateObj = new Date(dateStr);
                const year = dateObj.getFullYear();
                const month = dateObj.getMonth() + 1; // 1-12
                const quarter = Math.ceil(month / 3);
                endpoint = `${API_URL}/api/reports/periodic/generate-quarterly?year=${year}&quarter=${quarter}`;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                // Refresh audit
                await fetchAudit();
            } else {
                const err = await response.json();
                alert(`Erreur: ${err.detail || 'Echec régénération'}`);
            }
        } catch (error) {
            console.error(error);
            alert("Erreur réseau");
        } finally {
            setProcessing(null);
        }
    };

    const handleViewReport = (item: ReportStatus) => {
        if (item.url) {
            const url = item.url.startsWith('http') ? item.url : `${API_URL}${item.url}`;
            setSelectedReportUrl(url);
        } else {
            alert("URL du rapport non disponible.");
        }
    };

    const StatusBadge = ({ status }: { status: string }) => {
        const isAvailable = status === 'AVAILABLE';
        return (
            <span style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 700,
                backgroundColor: isAvailable ? '#ecfdf5' : '#fef2f2',
                color: isAvailable ? '#10b981' : '#ef4444',
                border: `1px solid ${isAvailable ? '#a7f3d0' : '#fecaca'}`
            }}>
                {isAvailable ? '✅ DISPONIBLE' : '❌ MANQUANT'}
            </span>
        );
    };

    const RenderSection = ({ title, data, type }: { title: string, data: ReportStatus[], type: string }) => {
        const missingCount = data.filter(d => d.status === 'MISSING').length;
        
        return (
            <Box style={{ marginBottom: '24px' }}>
                <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                        {title}
                        <span style={{ marginLeft: 12, fontSize: 13, fontWeight: 'normal', color: '#64748b' }}>
                            ({data.length} rapports attendus)
                        </span>
                    </h3>
                    {missingCount > 0 && (
                        <span style={{ padding: '4px 10px', backgroundColor: '#fef2f2', color: '#ef4444', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                            {missingCount} manquants
                        </span>
                    )}
                </div>
                
                <div style={{ padding: '20px' }}>
                    {data.length === 0 ? (
                        <p style={{ color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>Aucune donnée attendue ou générée.</p>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                            {data.map((item) => (
                                <div key={item.date} style={{
                                    border: `1px solid ${item.status === 'AVAILABLE' ? '#e2e8f0' : '#fecaca'}`,
                                    borderRadius: '8px',
                                    padding: '16px',
                                    backgroundColor: item.status === 'AVAILABLE' ? '#ffffff' : '#fff5f5',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '12px',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ fontWeight: 600, color: '#334155', fontSize: '14px' }}>
                                            📅 {item.label}
                                        </div>
                                        <StatusBadge status={item.status} />
                                    </div>
                                    
                                    <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                                        {item.status === 'MISSING' && (
                                            <button
                                                onClick={() => regenerateReport(type as any, item.date)}
                                                disabled={!!processing}
                                                style={{
                                                    padding: '6px 14px',
                                                    backgroundColor: processing === item.date ? '#cbd5e1' : '#3b82f6',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: processing === item.date ? 'not-allowed' : 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: 500
                                                }}
                                            >
                                                {processing === item.date ? '⏳ Génération...' : '⚙️ Générer maintenant'}
                                            </button>
                                        )}
                                        {item.status === 'AVAILABLE' && (
                                            <button
                                                onClick={() => handleViewReport(item)}
                                                style={{
                                                    padding: '6px 14px',
                                                    backgroundColor: 'transparent',
                                                    color: '#3b82f6',
                                                    border: '1px solid #bfdbfe',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: 500
                                                }}
                                            >
                                                👁️ Visualiser PDF
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Box>
        );
    };

    const GlobalModal = ({ url, onClose }: { url: string, onClose: () => void }) => (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)',
            display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }} onClick={onClose}>
            <div style={{
                background: 'white', borderRadius: '12px', width: '90%', maxWidth: '1000px', height: '90vh',
                display: 'flex', flexDirection: 'column', position: 'relative', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }} onClick={e => e.stopPropagation()}>
                
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>Visualiseur de Rapport</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}>×</button>
                </div>
                
                <div style={{ flex: 1, padding: 0, background: '#f1f5f9' }}>
                    <iframe src={url} width="100%" height="100%" title="Report Viewer" style={{ border: 'none' }} />
                </div>
            </div>
        </div>
    );

    return (
        <SiteLayout>
            <Header icon="assessment" title="Audit des Rapports BRVM" />
            <div style={{ padding: '12px 24px', width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <p style={{ color: '#64748b', margin: 0 }}>
                        Suivi des générations automatiques (Aperçu Quotidien, Hebdos, Mensuels, Trimestriels) sur 90 jours glissants.
                    </p>
                    <button 
                        onClick={fetchAudit}
                        disabled={loading}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '10px 16px', backgroundColor: '#ffffff',
                            border: '1px solid #cbd5e1', borderRadius: '8px',
                            cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 500, color: '#334155'
                        }}
                    >
                        {loading ? '⏳ Actualisation...' : '🔄 Actualiser l\'Audit'}
                    </button>
                </div>

                {loading && !auditData ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
                        Chargement des données d'audit...
                    </div>
                ) : auditData ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                        <RenderSection title="Aperçus Quotidiens du Marché" data={auditData.daily} type="DAILY" />
                        <RenderSection title="Rapports Hebdomadaires" data={auditData.weekly} type="WEEKLY" />
                        <RenderSection title="Bulletins Mensuels" data={auditData.monthly} type="MONTHLY" />
                        <RenderSection title="Analyses Trimestrielles" data={auditData.quarterly} type="QUARTERLY" />
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#ef4444' }}>
                        Erreur de chargement. Veuillez réessayer.
                    </div>
                )}

                {selectedReportUrl && <GlobalModal url={selectedReportUrl} onClose={() => setSelectedReportUrl(null)} />}
            </div>
        </SiteLayout>
    );
};

export default AdminReportsPage;
